import {
  Avatar,
  Box,
  Chip,
  Link as MuiLink,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DevicesIcon from '@mui/icons-material/Devices';
import { Link as RouterLink, useNavigate } from 'react-router';
import { getDeviceList } from 'src/api/queries';
import CustomizableList from '../core/customizable-list/CustomizableList';
import { stringOperators } from 'src/constants/TableConstants';
import { companyAutocompleteFilter } from 'src/utils/filterHelpers';

const tableHeaders = [
  { machineName: 'fiscal_id', humanName: 'Device', sortable: true },
  { machineName: 'serial_id', humanName: 'Serial', sortable: true },
  { machineName: 'owner__name', humanName: 'Owner' },
  { machineName: 'status', humanName: 'Status' },
  { machineName: 'id', humanName: 'ID', sortable: true, style: { align: 'right' } },
  { machineName: 'actions', humanName: '', style: { align: 'right' } },
];

const filterHeaders = [
  { machineName: 'fiscal_id', humanName: 'Fiscal ID', operators: stringOperators, inputType: 'text' },
  { machineName: 'ro_fiscal_id', humanName: 'RO Fiscal ID', operators: stringOperators, inputType: 'text' },
  { machineName: 'serial_id', humanName: 'Serial ID', operators: stringOperators, inputType: 'text' },
  companyAutocompleteFilter('owner__name', 'Owner Company Name'),
];

const ModelTypeDevicesList = ({ modelType }) => {
  const navigate = useNavigate();

  const queryDef = {
    key: [getDeviceList.key, 'model-type', modelType.id],
    func: (params) =>
      getDeviceList.func(
        params ? `model_type=${modelType.id}&${params}` : `model_type=${modelType.id}`
      ),
  };

  const customRowRenderer = (data) => {
    const detailHref = `/dashboard/devices/${data.fiscal_id}`;
    // Plain left-click anywhere on the row navigates in-app. Let modified or
    // non-primary clicks fall through (do nothing here) so the link can open the
    // detail in a new tab — Ctrl/Cmd+click and middle-click.
    const goToDetail = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      navigate(detailHref);
    };
    return (
    <TableRow
      key={`devices-${data.id}`}
      hover
      onClick={goToDetail}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'action.hover', color: 'text.secondary', width: 36, height: 36 }}>
            <DevicesIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <MuiLink
              component={RouterLink}
              to={detailHref}
              onClick={(e) => e.stopPropagation()}
              underline="hover"
              color="inherit"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {data.fiscal_id || '—'}
            </MuiLink>
            {data.ro_fiscal_id && (
              <Typography variant="caption" color="text.secondary">
                RO {data.ro_fiscal_id}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {data.serial_id || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={data.owner?.name ? 'text.primary' : 'text.disabled'}>
          {data.owner?.name || '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={data.status ? 'Active' : 'Inactive'}
          color={data.status ? 'success' : 'default'}
          variant={data.status ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption" color="text.secondary">
          #{data.id}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      </TableCell>
    </TableRow>
    );
  };

  return (
    <CustomizableList
      title={`Devices with Model Type "${modelType.name}"`}
      tableHeaders={tableHeaders}
      filterHeaders={filterHeaders}
      customRowRenderer={customRowRenderer}
      queryDef={queryDef}
    />
  );
};

export default ModelTypeDevicesList;
